import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import Papa from "papaparse";
import type { SourcePreset } from "./sourceMapping";

interface Props {
    sourceKey: SourcePreset["key"];
    onParsed: (headers: string[], rows: Record<string, string>[]) => void;
    onBack: () => void;
}

export function ImportUploadStep({ sourceKey: _sourceKey, onParsed, onBack }: Props) {
    const [error, setError] = useState<string | null>(null);
    const [parsing, setParsing] = useState(false);

    const handleFile = useCallback((file: File) => {
        if (!file.name.endsWith(".csv")) {
            setError("Please upload a .csv file");
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setError("File exceeds 10MB limit. Please split into smaller files.");
            return;
        }
        setError(null);
        setParsing(true);
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                setParsing(false);
                if (results.errors.length > 0 && results.data.length === 0) {
                    setError(`CSV parse error: ${results.errors[0].message}`);
                    return;
                }
                const headers = results.meta.fields || [];
                onParsed(headers, results.data as Record<string, string>[]);
            },
            error: (err) => {
                setParsing(false);
                setError(`Parse error: ${err.message}`);
            },
        });
    }, [onParsed]);

    return (
        <div>
            <h2 className="mb-4 text-lg font-semibold">Upload CSV File</h2>
            <div
                className="flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/25 p-8 transition-colors hover:border-primary/50"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const file = e.dataTransfer.files[0]; if (file) handleFile(file); }}
                onClick={() => { const input = document.createElement("input"); input.type = "file"; input.accept = ".csv"; input.onchange = (e) => { const file = (e.target as HTMLInputElement).files?.[0]; if (file) handleFile(file); }; input.click(); }}
            >
                {parsing ? (
                    <p className="text-muted-foreground">Parsing CSV...</p>
                ) : (
                    <>
                        <p className="text-muted-foreground">Drop a CSV file here or click to browse</p>
                        <p className="mt-1 text-xs text-muted-foreground">Max 10MB</p>
                    </>
                )}
            </div>
            {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
            <div className="mt-4">
                <Button variant="outline" onClick={onBack}>Back</Button>
            </div>
        </div>
    );
}
